/**
 * Settings subsection: lets a signed-in user change their account password.
 * Calls POST /api/v1/auth/change-password (verifies the current password).
 */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { apiService, ApiError } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { validatePasswordStrength } from '../../utils/passwordValidation';

interface ChangePasswordSectionProps {
  /** When true, render bare content (no outer Card/title) for embedding in an accordion. */
  embedded?: boolean;
}

export default function ChangePasswordSection({ embedded = false }: ChangePasswordSectionProps) {
  const { token: authToken } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const passwordError = useMemo(() => validatePasswordStrength(newPassword), [newPassword]);
  const mismatch = confirmPassword !== '' && newPassword !== confirmPassword;
  const sameAsCurrent = currentPassword !== '' && newPassword !== '' && currentPassword === newPassword;
  const canSubmit =
    !!currentPassword &&
    !!newPassword &&
    !!confirmPassword &&
    !passwordError &&
    !mismatch &&
    !sameAsCurrent &&
    !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await apiService.changePassword(currentPassword, newPassword, authToken || undefined);
      if (res.data?.success) {
        setSuccess('Your password has been updated.');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(res.data?.message || 'Could not update your password.');
      }
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? (err.status === 400
              ? 'Current password is incorrect, or the new password is invalid.'
              : err.message)
          : 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const formContent = (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 440 }}>
        <TextField
          fullWidth
          margin="normal"
          type="password"
          label="Current password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          disabled={submitting}
        />
        <TextField
          fullWidth
          margin="normal"
          type="password"
          label="New password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={submitting}
          error={(newPassword !== '' && !!passwordError) || sameAsCurrent}
          helperText={
            sameAsCurrent
              ? 'New password must be different from your current password.'
              : newPassword !== '' && passwordError
                ? passwordError
                : 'At least 8 characters, with an uppercase letter, a lowercase letter, and a number.'
          }
        />
        <TextField
          fullWidth
          margin="normal"
          type="password"
          label="Confirm new password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={submitting}
          error={mismatch}
          helperText={mismatch ? 'Passwords do not match' : ''}
        />
        <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={!canSubmit}>
          {submitting ? <CircularProgress size={24} /> : 'Update password'}
        </Button>
      </Box>
    </>
  );

  if (embedded) {
    return <Box sx={{ px: 2, pb: 2 }}>{formContent}</Box>;
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <LockIcon sx={{ mr: 1 }} color="primary" />
            <Box>
              <Typography variant="h6">Password</Typography>
              <Typography variant="body2" color="text.secondary">
                Change the password you use to sign in.
              </Typography>
            </Box>
          </Box>
          {formContent}
        </CardContent>
      </Card>
    </Box>
  );
}
